import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { visualColors } from '../colors';
export interface SymmetryGridProps {
  /** Grid size (rows × cols) */
  rows?: number;
  cols?: number;
  /** Cells that are shaded (indices 0-based, row-major) */
  shadedCells?: number[];
  /** Axis of symmetry: 'vertical' | 'horizontal' | 'diagonal' */
  axis?: 'vertical' | 'horizontal' | 'diagonal';
  /** Whether to show the mirror reflection */
  showMirror?: boolean;
  /** Label text */
  label?: string;
  /** Display mode */
  mode?: 'stimulus' | 'support';
}
export function SymmetryGrid({
  rows = 6,
  cols = 6,
  shadedCells = [],
  axis = 'vertical',
  showMirror = false,
  label,
  mode = 'support'
}: SymmetryGridProps) {
  const cellSize = 40;
  const gap = 2;
  const gridWidth = cols * (cellSize + gap) - gap;
  const gridHeight = rows * (cellSize + gap) - gap;
  const shadedSet = useMemo(() => new Set(shadedCells), [shadedCells]);
  // Calculate mirrored cells
  const mirroredCells = useMemo(() => {
    if (!showMirror) return new Set<number>();
    const mirrored = new Set<number>();
    for (const idx of shadedCells) {
      const row = Math.floor(idx / cols);
      const col = idx % cols;
      let mirrorRow = row;
      let mirrorCol = col;
      if (axis === 'vertical') {
        mirrorCol = cols - 1 - col;
      } else if (axis === 'horizontal') {
        mirrorRow = rows - 1 - row;
      } else {
        // diagonal: swap row and col
        mirrorRow = col;
        mirrorCol = row;
      }
      if (mirrorRow >= 0 && mirrorRow < rows && mirrorCol >= 0 && mirrorCol < cols) {
        mirrored.add(mirrorRow * cols + mirrorCol);
      }
    }
    return mirrored;
  }, [shadedCells, showMirror, axis, rows, cols]);
  const axisLabel = axis === 'vertical' ? 'verticale' : axis === 'horizontal' ? 'horizontale' : 'diagonale';
  return <div className={`${mode === 'support' ? 'bg-gradient-to-br from-purple-50 to-indigo-50 p-6 rounded-2xl border-2 border-purple-200 shadow-sm' : 'bg-purple-50 rounded-xl px-4 pt-3 pb-5 border border-purple-200'} w-full overflow-hidden`} data-id="element-14007" data-mp="1v39bb365e5aa">
      {label && <p className={`text-sm font-semibold text-purple-800 mb-3 text-center ${mode === 'support' ? 'text-base' : ''}`} data-id="element-14008" data-mp="258r5c9vxwp01">
          {label}
        </p>}

      <div className="flex justify-center w-full" aria-label={`Symmetrierooster met ${axisLabel} spiegelas`} data-id="element-14009" data-mp="1va1libkhguh5">
        <svg viewBox={`-10 -10 ${gridWidth + 20} ${gridHeight + 20}`} style={{
        width: '100%',
        height: 'auto',
        maxWidth: gridWidth + 20
      }} className="max-w-full" data-id="element-14010" data-mp="209egxkpf79vc">
          {/* Grid cells */}
          {Array.from({
          length: rows * cols
        }, (_, idx) => {
          const row = Math.floor(idx / cols);
          const col = idx % cols;
          const x = col * (cellSize + gap);
          const y = row * (cellSize + gap);
          const isShaded = shadedSet.has(idx);
          const isMirrored = mirroredCells.has(idx);
          return <motion.rect key={idx} x={x} y={y} width={cellSize} height={cellSize} rx={4} fill={isShaded ? '#7c3aed' : isMirrored ? '#a78bfa' : '#f5f3ff'} stroke={isShaded || isMirrored ? '#6d28d9' : '#c4b5fd'} strokeWidth={isShaded || isMirrored ? 2 : 1} initial={{
            scale: 0,
            opacity: 0
          }} animate={{
            scale: 1,
            opacity: 1
          }} transition={{
            delay: idx * 0.008,
            type: 'spring',
            stiffness: 400,
            damping: 25
          }} data-id="element-14011" data-mp="1ishct1jb63o4" />;
        })}

          {/* Symmetry axis line */}
          {axis === 'vertical' && <motion.line x1={gridWidth / 2} y1={-6} x2={gridWidth / 2} y2={gridHeight + 6} stroke={visualColors.axisGuide} strokeWidth={3} strokeDasharray="6 4" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} data-id="element-14012" data-mp="dtu5defasags" />}
          {axis === 'horizontal' && <motion.line x1={-6} y1={gridHeight / 2} x2={gridWidth + 6} y2={gridHeight / 2} stroke={visualColors.axisGuide} strokeWidth={3} strokeDasharray="6 4" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} data-id="element-14013" data-mp="2yjjkztnts0aj" />}
          {axis === 'diagonal' && <motion.line x1={-6} y1={-6} x2={gridWidth + 6} y2={gridHeight + 6} stroke={visualColors.axisGuide} strokeWidth={3} strokeDasharray="6 4" initial={{
          pathLength: 0
        }} animate={{
          pathLength: 1
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} data-id="element-14014" data-mp="2czhink4o1ekh" />}
        </svg>
      </div>

      {/* Legend */}
      {mode === 'support' && <div className="mt-4 flex items-center justify-center gap-6 text-xs text-slate-600 flex-wrap" data-id="element-14015" data-mp="1ep1jbtsewph8">
          <div className="flex items-center gap-1.5" data-id="element-14016" data-mp="1jnmng4asbtay">
            <div className="w-4 h-4 rounded bg-purple-600 border border-purple-700" data-id="element-14017" data-mp="1jp61do8yxs9k" />
            <span data-id="element-14018" data-mp="15peppu5xcnyq">Origineel</span>
          </div>
          {showMirror && <div className="flex items-center gap-1.5" data-id="element-14019" data-mp="1ameqbpk08yig">
              <div className="w-4 h-4 rounded bg-purple-400 border border-purple-500" data-id="element-14020" data-mp="1ap1446xhoa94" />
              <span data-id="element-14021" data-mp="1yevgyoht5ytd">Spiegeling</span>
            </div>}
          <div className="flex items-center gap-1.5" data-id="element-14022" data-mp="22d2n6penbhqb">
            <div className="w-4 h-1 border-t-2 border-dashed border-purple-500" data-id="element-14023" data-mp="1omz6edyjcg89" />
            <span data-id="element-14024" data-mp="1ol4wrvspsajh">{axisLabel} spiegelas</span>
          </div>
        </div>}
    </div>;
}